import {
  ALLERGY_CLINICAL_STATUSES,
  ALLERGY_CRITICALITIES,
  ALLERGY_REACTION_SEVERITIES,
  CONSULTATION_DIAGNOSIS_CERTAINTIES as DIAGNOSIS_CERTAINTIES,
  CONSULTATION_DIAGNOSIS_RANKS as DIAGNOSIS_RANKS,
  DIAGNOSIS_STATUSES,
  FINISH_RULES,
  NOTE_MAX_SECTIONS,
  NOTE_SECTION_MAX_CHARS,
  NOTE_STATUSES,
  VISIT_STATUSES,
  VISIT_TYPES,
} from '@wodi/domain';
import { z } from 'zod';

import { actorSchema, isoDaySchema, reasonSchema } from './clinical-common.ts';
import { allergySummarySchema } from './patients.ts';
import { terminologyCodingSchema } from './terminology.ts';
import { allergyRecordSchema, triageAssessmentSchema, vitalSignSetSchema } from './triage.ts';

// The consultation workspace (docs/plans/phase1-consultation.md §3.3, C1): consultations,
// notes, diagnoses, the problem list, allergy review, finishing and closing. Note text,
// diagnosis words and reasons are PHI: they travel only in bodies, never in URLs, logs or
// audit metadata.

export { DIAGNOSIS_CERTAINTIES, DIAGNOSIS_RANKS, DIAGNOSIS_STATUSES, FINISH_RULES, NOTE_STATUSES };

/**
 * The code system key diagnoses are searched in (terminology plan §2.7). Wodi's own key
 * for the loaded ICD-11 MMS release; which release is active is the terminology slice's.
 */
export const DIAGNOSIS_CODE_SYSTEM_KEYS = ['icd11_mms'] as const;

// --- Notes --------------------------------------------------------------------------------

export const noteSectionSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{0,39}$/, 'Not a section key'),
  heading: z.string().trim().min(1).max(200),
  text: z.string().max(NOTE_SECTION_MAX_CHARS),
});
export type NoteSectionInput = z.infer<typeof noteSectionSchema>;

export const noteSectionsSchema = z.array(noteSectionSchema).min(1).max(NOTE_MAX_SECTIONS);

export const noteTemplateSectionSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{0,39}$/),
  heading: z.string().trim().min(1).max(200),
  hint: z.string().max(500).nullable(),
  defaultText: z.string().max(NOTE_SECTION_MAX_CHARS).nullable(),
});

export const noteTemplateSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  version: z.number().int().min(1),
  /** Not yet approved by the clinical lead. */
  provisional: z.boolean(),
  sections: z.array(noteTemplateSectionSchema),
});
export type NoteTemplateView = z.infer<typeof noteTemplateSchema>;

export const noteSchema = z.object({
  id: z.uuid(),
  consultationId: z.uuid(),
  author: actorSchema,
  status: z.enum(NOTE_STATUSES),
  template: z.object({ id: z.uuid(), version: z.number().int() }).nullable(),
  signedAt: z.iso.datetime().nullable(),
  currentVersion: z.number().int().min(0),
  /**
   * The latest signed version, or the draft for its author. Null for a draft someone
   * else is writing: drafts are not records and are shown only to their author.
   */
  sections: noteSectionsSchema.nullable(),
  draftSavedAt: z.iso.datetime().nullable(),
  latestAmendment: z
    .object({ at: z.iso.datetime(), by: actorSchema, reason: z.string() })
    .nullable(),
  statusReason: z.string().nullable(),
});
export type NoteView = z.infer<typeof noteSchema>;

export const saveNoteDraftRequestSchema = z.object({
  sections: noteSectionsSchema,
  templateId: z.uuid().nullable().default(null),
});
export const saveNoteDraftResultSchema = z.object({
  noteId: z.uuid(),
  savedAt: z.iso.datetime(),
});

export const signNoteRequestSchema = z.object({
  sections: noteSectionsSchema,
  templateId: z.uuid().nullable().default(null),
});

export const amendNoteRequestSchema = z.object({
  sections: noteSectionsSchema,
  reason: reasonSchema,
});

export const noteVersionSchema = z.object({
  version: z.number().int().min(1),
  kind: z.enum(['signed', 'amendment']),
  sections: noteSectionsSchema,
  changeReason: z.string().nullable(),
  recordedAt: z.iso.datetime(),
  recordedBy: actorSchema,
  sha256: z.string(),
});
export type NoteVersionView = z.infer<typeof noteVersionSchema>;

export const noteHistorySchema = z.object({
  noteId: z.uuid(),
  status: z.enum(NOTE_STATUSES),
  versions: z.array(noteVersionSchema),
});

// --- Consultations ----------------------------------------------------------------------------

export const CONSULTATION_STATUSES = ['in_progress', 'finished', 'entered_in_error'] as const;

/**
 * Starting a consultation. From a queue: the queue place, which moves to "being seen" in
 * the same transaction. Otherwise the consultation room it happens in. Starting again
 * while the clinician's consultation on this visit is in progress returns that one.
 */
export const startConsultationRequestSchema = z
  .object({
    clientRequestId: z.uuid(),
    queueEntryId: z.uuid().optional(),
    servicePointId: z.uuid().optional(),
  })
  .refine((r) => (r.queueEntryId === undefined) !== (r.servicePointId === undefined), {
    message: 'Start from a queue place, or name the room',
  });

export const consultationSchema = z.object({
  id: z.uuid(),
  visitId: z.uuid(),
  /** A consultation in a room, or a ward round on an inpatient stay. */
  kind: z.enum(['consultation', 'ward_round']),
  /** The room; null for a ward round. */
  servicePoint: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  /** The ward a ward round was held in; null for a consultation. */
  ward: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  clinician: actorSchema,
  startedAt: z.iso.datetime(),
  finishedAt: z.iso.datetime().nullable(),
  status: z.enum(CONSULTATION_STATUSES),
  statusReason: z.string().nullable(),
});
export type ConsultationView = z.infer<typeof consultationSchema>;

// --- Diagnoses --------------------------------------------------------------------------------

export const diagnosisSchema = z.object({
  id: z.uuid(),
  visitId: z.uuid(),
  consultationId: z.uuid().nullable(),
  /** Null for an uncoded diagnosis ("Not coded"), allowed only while ICD-11 isn't set up. */
  coding: terminologyCodingSchema.nullable(),
  clinicianText: z.string().nullable(),
  rank: z.enum(DIAGNOSIS_RANKS),
  certainty: z.enum(DIAGNOSIS_CERTAINTIES),
  status: z.enum(DIAGNOSIS_STATUSES),
  diagnosedAt: z.iso.datetime(),
  recordedAt: z.iso.datetime(),
  recordedBy: actorSchema,
  replacesId: z.uuid().nullable(),
  statusReason: z.string().nullable(),
});
export type DiagnosisView = z.infer<typeof diagnosisSchema>;

const clinicianTextSchema = z.string().trim().max(500);

export const recordDiagnosisRequestSchema = z
  .object({
    coding: terminologyCodingSchema.nullable(),
    clinicianText: clinicianTextSchema.nullable().default(null),
    rank: z.enum(DIAGNOSIS_RANKS),
    certainty: z.enum(DIAGNOSIS_CERTAINTIES),
    /** When the clinician made it; absent means now. Not before arrival, not in the future. */
    diagnosedAt: z.iso.datetime().optional(),
  })
  .refine((d) => d.coding !== null || (d.clinicianText ?? '').trim() !== '', {
    message: 'Choose a code, or describe the diagnosis in words',
  });
export type RecordDiagnosisRequest = z.input<typeof recordDiagnosisRequestSchema>;

export const correctDiagnosisRequestSchema = z.object({
  rank: z.enum(DIAGNOSIS_RANKS).optional(),
  certainty: z.enum(DIAGNOSIS_CERTAINTIES).optional(),
  coding: terminologyCodingSchema.nullable().optional(),
  clinicianText: clinicianTextSchema.nullable().optional(),
  reason: reasonSchema,
});
export type CorrectDiagnosisRequest = z.input<typeof correctDiagnosisRequestSchema>;

// --- Problem list -----------------------------------------------------------------------------

export const PROBLEM_CLINICAL_STATUSES = ['active', 'inactive', 'resolved'] as const;

export const problemSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  coding: terminologyCodingSchema.nullable(),
  clinicianText: z.string().nullable(),
  clinicalStatus: z.enum(PROBLEM_CLINICAL_STATUSES),
  onsetDate: z.string().nullable(),
  onsetEstimated: z.boolean(),
  abatementDate: z.string().nullable(),
  fromDiagnosisId: z.uuid().nullable(),
  status: z.enum(['active', 'entered_in_error']),
  statusReason: z.string().nullable(),
  version: z.number().int().min(1),
  recordedAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  updatedBy: actorSchema,
});
export type ProblemView = z.infer<typeof problemSchema>;

export const recordProblemRequestSchema = z
  .object({
    coding: terminologyCodingSchema.nullable(),
    clinicianText: clinicianTextSchema.nullable().default(null),
    clinicalStatus: z.enum(PROBLEM_CLINICAL_STATUSES).default('active'),
    onsetDate: isoDaySchema.nullable().default(null),
    onsetEstimated: z.boolean().default(false),
    /** "Add to problem list" from a diagnosis of this visit. */
    fromDiagnosisId: z.uuid().nullable().default(null),
  })
  .refine(
    (p) => p.fromDiagnosisId !== null || p.coding !== null || (p.clinicianText ?? '').trim() !== '',
    { message: 'Choose a code, or describe the problem in words' },
  );
export type RecordProblemRequest = z.input<typeof recordProblemRequestSchema>;

export const updateProblemRequestSchema = z.object({
  /** The visit the change is made in (domain rule 1). */
  visitId: z.uuid(),
  expectedVersion: z.number().int().min(1),
  clinicalStatus: z.enum(PROBLEM_CLINICAL_STATUSES).optional(),
  onsetDate: isoDaySchema.nullable().optional(),
  abatementDate: isoDaySchema.nullable().optional(),
  reason: reasonSchema,
});

export const problemEnteredInErrorRequestSchema = z.object({
  visitId: z.uuid(),
  reason: reasonSchema,
});

// --- Allergy review ---------------------------------------------------------------------------

export const ALLERGEN_KINDS = ['substance', 'allergen_group'] as const;

export const allergyReviewRecordSchema = allergyRecordSchema.extend({
  /** What a clinician coded the allergy to, so prescribing can check it. */
  allergen: z
    .object({ kind: z.enum(ALLERGEN_KINDS), code: z.string(), display: z.string() })
    .nullable(),
  verifiedBy: actorSchema.nullable(),
  verifiedAt: z.iso.datetime().nullable(),
  version: z.number().int().min(1),
});
export type AllergyReviewRecordView = z.infer<typeof allergyReviewRecordSchema>;

export const allergyReviewRequestSchema = z
  .object({
    /** The visit the review is made in (domain rule 1). */
    visitId: z.uuid(),
    verificationStatus: z.enum(['unconfirmed', 'confirmed', 'refuted']).optional(),
    clinicalStatus: z.enum(ALLERGY_CLINICAL_STATUSES).optional(),
    reactionSeverity: z.enum(ALLERGY_REACTION_SEVERITIES).nullable().optional(),
    criticality: z.enum(ALLERGY_CRITICALITIES).nullable().optional(),
    /** A concept of the tenant's `drug_substance` or `allergen_group` value set. */
    allergen: z
      .object({
        kind: z.enum(ALLERGEN_KINDS),
        system: z.string().trim().min(1).max(200),
        code: z.string().trim().min(1).max(64),
      })
      .optional(),
    reason: reasonSchema,
  })
  .refine(
    (r) =>
      r.verificationStatus !== undefined ||
      r.clinicalStatus !== undefined ||
      r.reactionSeverity !== undefined ||
      r.criticality !== undefined ||
      r.allergen !== undefined,
    { message: 'Change at least one thing about the allergy' },
  );
export type AllergyReviewRequest = z.input<typeof allergyReviewRequestSchema>;

export const allergenSearchRequestSchema = z.object({
  query: z.string().trim().min(2).max(100),
});
export const allergenOptionSchema = z.object({
  kind: z.enum(ALLERGEN_KINDS),
  system: z.string(),
  code: z.string(),
  display: z.string(),
});
export const allergenSearchResultSchema = z.object({ items: z.array(allergenOptionSchema) });

export const allergyHistorySchema = z.object({
  allergyId: z.uuid(),
  versions: z.array(
    z.object({
      version: z.number().int().min(1),
      changedAt: z.iso.datetime(),
      changedBy: actorSchema,
      verificationStatus: z.string(),
      clinicalStatus: z.string(),
      severity: z.string().nullable(),
      criticality: z.string().nullable(),
      allergen: z.string().nullable(),
      reason: z.string().nullable(),
    }),
  ),
});

// --- Workspace --------------------------------------------------------------------------------

export const CONSENT_STATES = ['granted', 'refused', 'withdrawn', 'not_asked'] as const;

export const previousVisitSchema = z.object({
  id: z.uuid(),
  visitNumber: z.string(),
  startedAt: z.iso.datetime(),
  diagnoses: z.array(z.string()),
  clinicians: z.array(z.string()),
});

/** Everything the consultation workspace shows for one visit, in one read (plan §3.3). */
export const workspaceSchema = z.object({
  visit: z.object({
    id: z.uuid(),
    patientId: z.uuid(),
    visitNumber: z.string(),
    status: z.enum(VISIT_STATUSES),
    type: z.enum(VISIT_TYPES),
    startedAt: z.iso.datetime(),
    endedAt: z.iso.datetime().nullable(),
  }),
  /** The visit's place in a queue, for "Send to" after finishing; null when not queued. */
  queueEntry: z
    .object({
      id: z.uuid(),
      servicePointId: z.uuid(),
      servicePointName: z.string(),
      status: z.enum(['waiting', 'called', 'in_service']),
    })
    .nullable(),
  /** The signed-in clinician's consultation in progress on this visit, if any. */
  myConsultationId: z.uuid().nullable(),
  consultations: z.array(consultationSchema),
  notes: z.array(noteSchema),
  /** The template a new note starts from, or null when none is set up. */
  template: noteTemplateSchema.nullable(),
  diagnoses: z.array(diagnosisSchema),
  problems: z.array(problemSchema),
  allergies: allergySummarySchema,
  allergyRecords: z.array(allergyReviewRecordSchema),
  triage: triageAssessmentSchema.nullable(),
  /** The latest active vital sign set of this visit. */
  vitals: vitalSignSetSchema.nullable(),
  previousVisits: z.array(previousVisitSchema),
  consent: z.object({
    state: z.enum(CONSENT_STATES),
    recordedAt: z.iso.datetime().nullable(),
    /** Every decision, newest first, read-only for clinicians (consultation plan P12). */
    history: z.array(
      z.object({
        decision: z.enum(['granted', 'refused', 'withdrawn']),
        recordedAt: z.iso.datetime(),
        reason: z.string().nullable(),
      }),
    ),
  }),
});
export type WorkspaceView = z.infer<typeof workspaceSchema>;

// --- Finish and close -------------------------------------------------------------------------

export const finishItemSchema = z.object({
  ruleId: z.enum(FINISH_RULES),
  tier: z.literal('interruptive'),
  message: z.string(),
});
export type FinishItemView = z.infer<typeof finishItemSchema>;

export const finishCheckSchema = z.object({ items: z.array(finishItemSchema) });

export const finishConsultationRequestSchema = z.object({
  acknowledgements: z
    .array(z.object({ ruleId: z.enum(FINISH_RULES), reason: reasonSchema }))
    .max(FINISH_RULES.length)
    .default([]),
});

export const CLOSE_BLOCKERS = [
  'consultation_in_progress',
  'in_queue',
  'patient_owes',
  'charges_not_priced',
] as const;
export const CLOSE_REASON_ITEMS = ['orders_open', 'no_diagnosis'] as const;

export const closeCheckSchema = z.object({
  blocking: z.array(z.object({ code: z.enum(CLOSE_BLOCKERS), message: z.string() })),
  needsReason: z.array(z.object({ code: z.enum(CLOSE_REASON_ITEMS), message: z.string() })),
});
export type CloseCheckView = z.infer<typeof closeCheckSchema>;

export const closeVisitRequestSchema = z.object({
  reason: reasonSchema.optional(),
});
export type FinishRule = (typeof FINISH_RULES)[number];
